import { InputHTMLAttributes, forwardRef } from 'react';
import './Input.css';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: InputSize;
  inputClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      size = 'md',
      id,
      className = '',
      inputClassName = '',
      ...rest
    },
    ref,
  ) => {
    const hasError = error != null && error !== '';
    const inputId =
      id ?? (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    const containerClass = [
      'input-field',
      fullWidth ? 'input-field--full-width' : '',
      hasError ? 'input-field--error' : '',
      rest.disabled ? 'input-field--disabled' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClass = [
      'input-field__control',
      `input-field__control--${size}`,
      inputClassName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClass}>
        {label != null && (
          <label className="input-field__label" htmlFor={inputId}>
            {label}
            {rest.required && (
              <span className="input-field__required" aria-hidden="true">
                {' '}*
              </span>
            )}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={inputClass}
          aria-describedby={
            hasError
              ? `${inputId}-error`
              : helperText
              ? `${inputId}-helper`
              : undefined
          }
          aria-invalid={hasError ? 'true' : 'false'}
          aria-required={rest.required ? 'true' : undefined}
          {...rest}
        />
        {hasError && (
          <span id={`${inputId}-error`} className="input-field__error" role="alert">
            {error}
          </span>
        )}
        {helperText != null && !hasError && (
          <span id={`${inputId}-helper`} className="input-field__helper">
            {helperText}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
